// /tasks endpoints (plan §4). Lists take the filters the API accepts; the query string is built
// in one fixed order so equal filters give equal URLs (and equal cache keys on the web).
import type { TaskAction, TaskStatus } from '@cis/shared';
import { createHttp, type Http, type HttpOptions } from './http.ts';
import { pathSegment } from './paths.ts';
import {
  historyPageSchema,
  taskDetailSchema,
  taskPageSchema,
  type HistoryPage,
  type TaskDetail,
  type TaskPage,
} from './schemas/tasks.ts';

export interface TaskListParams {
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
  readonly phase?: string | undefined;
  readonly area?: string | undefined;
  readonly family?: number | undefined;
  readonly tier?: string | undefined;
  readonly assignee?: 'me' | number | undefined;
  readonly status?: readonly TaskStatus[] | undefined;
  readonly dueFrom?: string | undefined;
  readonly dueTo?: string | undefined;
  readonly q?: string | undefined;
  readonly view?: 'my-week' | 'overdue' | undefined;
  readonly includeRetired?: boolean | undefined;
}

type Encoder = (value: never) => string | undefined;

// One entry per API parameter, in URL order. Empty values are left out (the API refuses them).
const ENCODERS: readonly (readonly [keyof TaskListParams, Encoder])[] = [
  ['page', (v: number) => String(v)],
  ['pageSize', (v: number) => String(v)],
  ['phase', (v: string) => v],
  ['area', (v: string) => v.trim() || undefined],
  ['family', (v: number) => String(v)],
  ['tier', (v: string) => v],
  ['assignee', (v: 'me' | number) => String(v)],
  ['status', (v: readonly TaskStatus[]) => (v.length === 0 ? undefined : v.join(','))],
  ['dueFrom', (v: string) => v],
  ['dueTo', (v: string) => v],
  ['q', (v: string) => v.trim() || undefined],
  ['view', (v: string) => v],
  ['includeRetired', (v: boolean) => (v ? 'true' : undefined)],
];

/** `?a=1&b=2` for the given filters, or '' when there are none. */
export function taskListQuery(params: TaskListParams): string {
  const search = new URLSearchParams();
  ENCODERS.forEach(([key, encode]) => {
    const value = params[key];
    if (value === undefined) return;
    const text = encode(value as never);
    if (text !== undefined) search.set(key, text);
  });
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}

// Actions a person can post (spec §5.2). Retirement comes only from a content pack.
export type UserTaskAction = Exclude<TaskAction, 'retire'>;

// Which body field carries the typed text: a reason for refusals and holds, a note otherwise.
const NOTE_ACTIONS: readonly UserTaskAction[] = ['verify', 'unblock'];

export interface ActOptions {
  readonly action: UserTaskAction;
  readonly version: number;
  /** The reason (block, defer, send back, reopen) or note (verify, unblock). */
  readonly text?: string | undefined;
}

export interface TickOptions {
  readonly stepNo: number;
  readonly ticked: boolean;
  readonly version: number;
}

function actionBody(opts: ActOptions): Record<string, string> {
  const text = opts.text?.trim() ?? '';
  if (text === '') return {};
  return NOTE_ACTIONS.includes(opts.action) ? { note: text } : { reason: text };
}

export interface AssignOptions {
  /** Everyone who works the task (or the step) after the change; [] leaves it unassigned. */
  readonly userIds: readonly number[];
  readonly version: number;
}

export interface ReplanOptions {
  /** A re-planned date, null to go back to the pack's date, left out to keep it. */
  readonly start?: string | null | undefined;
  readonly end?: string | null | undefined;
  readonly version: number;
}

export interface EditOptions {
  /** Update notes; '' or null clears them, left out keeps them. */
  readonly updateNotes?: string | null | undefined;
  readonly actualFinish?: string | null | undefined;
  /** The owner's override with a note; null goes back to the steps ticked. */
  readonly percentOverride?: { readonly percent: number; readonly note: string } | null | undefined;
  readonly version: number;
}

/** The fields that were given, so an omitted field is left as it is on the server. */
function givenFields(fields: Readonly<Record<string, unknown>>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));
}

const HISTORY_PAGE_SIZE = 20;

export interface TasksApi {
  /** GET /tasks: one page of tasks with the total that match the filters. */
  list(params: TaskListParams, signal?: AbortSignal): Promise<TaskPage>;
  /** GET /tasks/:taskId, or the page rebuilt from history at the end of `asOf` (read-only). */
  get(
    taskId: string,
    opts?: { readonly asOf?: string | undefined; readonly signal?: AbortSignal | undefined },
  ): Promise<TaskDetail>;
  /** POST /tasks/:taskId/actions/:action with If-Match; answers the updated task page. */
  act(taskId: string, opts: ActOptions): Promise<TaskDetail>;
  /** PUT /tasks/:taskId/steps/:no/tick with If-Match; answers the updated task page. */
  tick(taskId: string, opts: TickOptions): Promise<TaskDetail>;
  /** PUT /tasks/:taskId/assignees with If-Match (spec J3); answers the updated task page. */
  assign(taskId: string, opts: AssignOptions): Promise<TaskDetail>;
  /** PUT /tasks/:taskId/steps/:no/assignees with If-Match; answers the updated task page. */
  assignStep(taskId: string, stepNo: number, opts: AssignOptions): Promise<TaskDetail>;
  /** PATCH /tasks/:taskId/plan with If-Match: re-planned start and end dates. */
  replan(taskId: string, opts: ReplanOptions): Promise<TaskDetail>;
  /** PATCH /tasks/:taskId with If-Match: update notes, actual finish, percent override. */
  edit(taskId: string, opts: EditOptions): Promise<TaskDetail>;
  /** GET /history/task/:taskId: the task's and its steps' changes, newest first. */
  history(
    taskId: string,
    opts?: { readonly page?: number; readonly signal?: AbortSignal | undefined },
  ): Promise<HistoryPage>;
}

type ReplanWrites = Pick<TasksApi, 'assign' | 'assignStep' | 'replan' | 'edit'>;

// Re-plan and edit writes (spec J3, M5-R3): each answers the updated task page.
function replanWrites(http: Http, taskPath: (taskId: string) => string): ReplanWrites {
  return {
    assign: async (taskId, opts) =>
      http.json(
        {
          method: 'PUT',
          path: `${taskPath(taskId)}/assignees`,
          version: opts.version,
          body: { userIds: [...opts.userIds] },
        },
        taskDetailSchema,
      ),
    assignStep: async (taskId, stepNo, opts) =>
      http.json(
        {
          method: 'PUT',
          path: `${taskPath(taskId)}/steps/${pathSegment(stepNo)}/assignees`,
          version: opts.version,
          body: { userIds: [...opts.userIds] },
        },
        taskDetailSchema,
      ),
    replan: async (taskId, opts) =>
      http.json(
        {
          method: 'PATCH',
          path: `${taskPath(taskId)}/plan`,
          version: opts.version,
          body: givenFields({ start: opts.start, end: opts.end }),
        },
        taskDetailSchema,
      ),
    edit: async (taskId, opts) =>
      http.json(
        {
          method: 'PATCH',
          path: taskPath(taskId),
          version: opts.version,
          body: givenFields({
            updateNotes: opts.updateNotes,
            actualFinish: opts.actualFinish,
            percentOverride: opts.percentOverride,
          }),
        },
        taskDetailSchema,
      ),
  };
}

export function createTasksApi(options: HttpOptions = {}): TasksApi {
  const http = createHttp(options);
  // Methods are async so a refused id is a rejected promise, never a synchronous throw.
  const taskPath = (taskId: string): string => `/tasks/${pathSegment(taskId)}`;
  return {
    list: (params, signal) =>
      http.json({ method: 'GET', path: `/tasks${taskListQuery(params)}`, signal }, taskPageSchema),
    get: async (taskId, opts = {}) => {
      const query = opts.asOf === undefined ? '' : `?${new URLSearchParams({ asOf: opts.asOf })}`;
      return http.json(
        { method: 'GET', path: `${taskPath(taskId)}${query}`, signal: opts.signal },
        taskDetailSchema,
      );
    },
    act: async (taskId, opts) =>
      http.json(
        {
          method: 'POST',
          path: `${taskPath(taskId)}/actions/${pathSegment(opts.action)}`,
          version: opts.version,
          body: actionBody(opts),
        },
        taskDetailSchema,
      ),
    tick: async (taskId, opts) =>
      http.json(
        {
          method: 'PUT',
          path: `${taskPath(taskId)}/steps/${pathSegment(opts.stepNo)}/tick`,
          version: opts.version,
          body: { ticked: opts.ticked },
        },
        taskDetailSchema,
      ),
    ...replanWrites(http, taskPath),
    history: async (taskId, opts = {}) => {
      const query = new URLSearchParams({
        page: String(opts.page ?? 1),
        pageSize: String(HISTORY_PAGE_SIZE),
      });
      return http.json(
        {
          method: 'GET',
          path: `/history/task/${pathSegment(taskId)}?${query}`,
          signal: opts.signal,
        },
        historyPageSchema,
      );
    },
  };
}

export * from './schemas/tasks.ts';
